
import { api, BudgetBucket, ExpenseDetails, ImportType, OutputExpense, withLoading } from "@/services/api";
import { formatDate } from "@/services/date";
import { visualizeAmount } from "@/services/visualizer";
import EditableField from "@/components/EditableField.vue";
import FileInput from "@/components/FileInput.vue";
import Tag from "@/components/Tag.vue";
import TagInput from "@/components/TagInput.vue";

import UpdateCreateExpense, {
    ExpenseEventData,
    DefaultExpense,
} from "@/components/UpdateCreateExpense.vue";

const Fragment = Vue.extend({
  functional: true,
  render: (h, ctx) => {
      return ctx.children;
  }
})

const emptyFilters = {
    from: undefined as string | undefined,
    to: undefined as string | undefined,
    min: "" as string,
    max: "" as string,
    description: "",
    tags: "",
    sensitive: false,
    showIncome: false,
    showImportedOnly: false,
    noBucket: false
};

const pageSize = 100;

import Vue from "vue";
export default Vue.extend({
    components: {
        EditableField,
        FileInput,
        UpdateCreateExpense,
        Fragment,
        Tag,
        TagInput
    },
    data() {
        return {
            rawExpenses: [] as OutputExpense[],
            budgetBuckets: [] as BudgetBucket[],
            importedIds: new Set<number>(),
            filters: {
                ...emptyFilters,
            },
            visualization: {
                showTags: false
            },
            editingExpense: undefined as DefaultExpense | undefined,
            tagAllList: [] as string[],
            resetTagInput: () => {
                //empty
            },
            taggingAll: false,
            showImportModal: false,
            lastImportedCount: null as number | null,
            currentPage: 0,
            expandedExpenseId: undefined as number | undefined,
            detailsLoadingId: undefined as number | undefined,
            detailsByExpense: {} as { [key: number]: ExpenseDetails },
        };
    },
    beforeMount() {
        this.setThisMonth();
    },
    methods: {
        async handleUpdatedExpense(expense: ExpenseEventData) {
            this.editingExpense = undefined;
            const [mainExpense] = this.rawExpenses.filter(
                (e) => e.id == expense.id
            );
            if (!mainExpense) {
                alert("Invalid expense id");
                return;
            }
            const date = expense.date ? new Date(expense.date) : mainExpense.date;
            const isDateUpdated = date.getFullYear() !== mainExpense.date.getFullYear() ||
                                  date.getMonth() !== mainExpense.date.getMonth() ||
                                  date.getDate() !== mainExpense.date.getDate();

            await api.updateExpense({
                amount: expense.amount,
                description: expense.description,
                id: mainExpense.id,
                date,
                tags: expense.tags,
                sensitive: expense.sensitive,
                setBudgetBucketOverride: expense.setBudgetBucketOverride,
                budgetBucketOverrideId: expense.budgetBucketOverrideId,
            });
            mainExpense.amount = mainExpense.isSplitChild ? mainExpense.amount : Math.floor(expense.amount * 100);
            mainExpense.description = expense.description;
            mainExpense.sensitive = mainExpense.isSplitChild ? mainExpense.sensitive : expense.sensitive;
            mainExpense.tags = expense.tags;
            mainExpense.date = mainExpense.isSplitChild ? mainExpense.date : date;
            if (expense.setBudgetBucketOverride) {
                mainExpense.budgetBucketOverride = this.bucketSummary(expense.budgetBucketOverrideId);
                mainExpense.budgetBucket = mainExpense.budgetBucketOverride || null;
                if (!expense.budgetBucketOverrideId) {
                    await this.refreshExpenses();
                    return;
                }
            }
            if (isDateUpdated) {
                this.rawExpenses = this.rawExpenses.sort((a, b) => b.date.getTime() - a.date.getTime());
            }
        },
        cancelUpdate() {
            this.editingExpense = undefined;
        },
        editExpense(id: number) {
            const [expense] = this.expenses.filter((e) => e.id == id);
            this.editingExpense = {
                description: expense.description,
                date: expense.date,
                amount: expense.rawAmount,
                id: expense.id,
                tags: expense.tags,
                sensitive: expense.sensitive,
                isSplitChild: expense.isSplitChild,
                budgetBucketOverrideId: expense.budgetBucketOverride?.id ?? null,
            };
        },
        async refreshExpenses() {
            const newExpenses = await api.listExpenses();
            this.rawExpenses = newExpenses;
        },
        async refreshBudgetBuckets() {
            this.budgetBuckets = (await api.getBudgetBuckets()).filter(bucket => bucket.isActive);
        },
        async loadJson(file: File) {
            await api.importJson(file);
            this.importedIds = new Set<number>();
            this.lastImportedCount = null;
            await this.refreshExpenses();
        },
        async load(file: File, importType: ImportType) {
            this.importedIds = new Set<number>(await api.import(file, importType));
            this.lastImportedCount = this.importedIds.size;
            this.filters.showImportedOnly = this.importedIds.size > 0;
            this.setAllTime();
            this.currentPage = 0;
            await this.refreshExpenses();
        },
        closeImportModal() {
            this.showImportModal = false;
        },
        async updateDescription(expense: OutputExpense, value: string) {
            expense.description = value;
            await api.updateExpense(expense);
        },
        async updateAmount(expense: OutputExpense, value: number) {
            expense.amount = value;
            await api.updateExpense(expense);
        },
        async remove(expense: OutputExpense) {
            await api.delete(expense);
            this.rawExpenses = this.rawExpenses.filter(
                (e) => e.id !== expense.id
            );
        },
        async removeExpense(id: number) {
            this.editingExpense = undefined;
            await api.delete(id);
            this.rawExpenses = this.rawExpenses.filter(e => e.id !== id);
        },
        setThisMonth() {
            const today = new Date();
            const from = new Date(today.getFullYear(), today.getMonth(), 1);
            const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            this.filters.from = formatDate(from);
            this.filters.to = formatDate(to);
        },
        setThisYear() {
            const today = new Date();
            const from = new Date(today.getFullYear(), 0, 1);
            const to = new Date(today.getFullYear() + 1, 0, 0);
            this.filters.from = formatDate(from);
            this.filters.to = formatDate(to);
        },
        setAllTime() {
            this.filters.from = undefined;
            this.filters.to = undefined;
        },
        clearFilters() {
            this.filters = { ...emptyFilters, from: this.filters.from, to: this.filters.to };
        },
        async autoTagAll() {
            const ids = this.filteredExpenses.map(e => e.id);
            try {
                await api.autoTagAll(ids);
            } finally {
                this.resetTagAll();
            }
            await this.refreshExpenses();
        },
        async autoMapAll() {
            const ids = this.filteredExpenses.map(e => e.id);
            try {
                await api.autoMapAll(ids);
            } finally {
                await this.refreshExpenses();
            }
        },
        async autoBucketAll() {
            const ids = this.filteredExpenses.map(e => e.id);
            try {
                await api.applyBudgetRules(ids);
            } finally {
                await this.refreshExpenses();
            }
        },
        async tagAll() {
            this.taggingAll = false;
            await withLoading(async () => {
                for (const expense of this.filteredExpenses) {
                    const [mainExpense] = this.rawExpenses.filter(e => e.id === expense.id);
                    if (!mainExpense) {
                        continue;
                    }
                    if (!mainExpense.sensitive || this.filters.sensitive) {
                        for (const tag of this.tagAllList) {
                            if (!mainExpense.tags.includes(tag)) {
                                mainExpense.tags.push(tag);
                            }
                        }
                        await api.updateExpense({
                            amount: expense.rawAmount / 100,
                            description: mainExpense.description,
                            date: mainExpense.date,
                            tags: mainExpense.tags,
                            sensitive: mainExpense.sensitive,
                            id: mainExpense.id
                        });
                    }
                }
            });
            this.resetTagAll();
        },
        addToAllTags(tag: string) {
            if (!this.tagAllList.includes(tag)) {
                this.tagAllList.push(tag);
            }
        },
        removeFromAllTags(tag: string) {
            this.tagAllList = this.tagAllList.filter(e => e !== tag);
        },
        resetTagAll() {
            this.tagAllList = [];
            this.resetTagInput();
            this.taggingAll = false;
        },
        startTagging() {
            this.taggingAll = true;
        },
        changePageTo(index: number | undefined) {
            if (index !== undefined) {
                this.currentPage = index;
            }
            setTimeout(() => {
                // (this.$refs['top'] as HTMLElement).scrollIntoView();
                // scrollDownByNavbar();
                window.scrollTo({top: 0});
            });//window.scrollTo(0, document.body.scrollHeight), 0);
        },
        money(amount: number): string {
            return visualizeAmount(amount);
        },
        formatDetailDate(value: string): string {
            return formatDate(new Date(value));
        },
        async unsplitExpense(expenseId: number) {
            await api.unsplitExpense(expenseId);
            this.expandedExpenseId = undefined;
            this.$delete(this.detailsByExpense, expenseId);
            await this.refreshExpenses();
        },
        async toggleDetails(expenseId: number) {
            if (this.expandedExpenseId === expenseId) {
                this.expandedExpenseId = undefined;
                return;
            }
            this.expandedExpenseId = expenseId;
            if (this.detailsByExpense[expenseId]) {
                return;
            }
            this.detailsLoadingId = expenseId;
            try {
                const details = await api.getExpenseDetails(expenseId);
                if (details) {
                    this.$set(this.detailsByExpense, expenseId, details);
                }
            } finally {
                this.detailsLoadingId = undefined;
            }
        },
        bucketSummary(bucketId: number | null | undefined) {
            if (!bucketId) {
                return null;
            }
            const bucket = this.budgetBuckets.find(item => item.id === bucketId);
            return bucket
                ? { id: bucket.id, name: bucket.name, slug: bucket.slug }
                : null;
        }
    },
    async mounted() {
        await this.refreshBudgetBuckets();
        await this.refreshExpenses();
    },
    watch: {
        filteredExpenses() {
            if (this.currentPage >= this.totalPages) {
                this.currentPage = Math.max(0, this.totalPages - 1);
            }
        }
    },
    computed: {
        expenses(): Array<{
            date: string;
            amount: string;
            rawAmount: number;
            description: string;
            rawDate: Date;
            id: number;
            tags: string[];
            sensitive: boolean;
            hasDetails?: boolean;
            budgetBucket?: { id: number; name: string; slug: string } | null;
            budgetBucketOverride?: { id: number; name: string; slug: string } | null;
            isSplitChild?: boolean;
        }> {
            return this.rawExpenses.map((e) => ({
                ...e,
                amount: visualizeAmount(e.amount),
                date: formatDate(e.date),
                rawAmount: e.amount,
                rawDate: e.date,
            }));
        },
        totalPages(): number {
            return Math.ceil(this.filteredExpenses.length / pageSize);
        },
        shownExpenses(): Array<{
            date: string;
            amount: string;
            description: string;
            rawAmount: number;
            id: number;
            sensitive: boolean;
            tags: string[];
            hasDetails?: boolean;
            budgetBucket?: { id: number; name: string; slug: string } | null;
            budgetBucketOverride?: { id: number; name: string; slug: string } | null;
            isSplitChild?: boolean;
        }> {
            const start = this.currentPage * pageSize;
            return this.filteredExpenses.slice(start, start + pageSize);
        },
        filteredExpenses(): Array<{
            date: string;
            amount: string;
            description: string;
            rawAmount: number;
            id: number;
            sensitive: boolean;
            tags: string[];
            hasDetails?: boolean;
            budgetBucket?: { id: number; name: string; slug: string } | null;
            budgetBucketOverride?: { id: number; name: string; slug: string } | null;
            isSplitChild?: boolean;
        }> {
            const filterFrom = this.filters.from && new Date(this.filters.from);
            if (filterFrom) {
                filterFrom.setHours(0, 0, 0, 0);
            }
            const filterTo = this.filters.to && new Date(this.filters.to);
            if (filterTo) {
                filterTo.setHours(23, 59, 59, 999);
            }
            const max =
                this.filters.max !== "" &&
                Math.ceil(parseFloat(this.filters.max) * 100);
            const min =
                this.filters.min !== "" &&
                Math.floor(parseFloat(this.filters.min) * 100);
            const desc =
                this.filters.description &&
                this.filters.description.toLowerCase().split("|").map(e => e.trim());

            const tags =
                this.filters.tags && this.filters.tags.toLowerCase().split("|").map(e => e.trim());
            return this.expenses.filter((e) => {
                if (filterFrom && filterFrom > e.rawDate) {
                    return false;
                }
                if (filterTo && filterTo < e.rawDate) {
                    return false;
                }
                if (max && max < e.rawAmount) {
                    return false;
                }
                if (min && min > e.rawAmount) {
                    return false;
                }
                if (!this.filters.showIncome && e.rawAmount < 0) {
                    return false;
                }
                if (this.filters.showImportedOnly && !this.importedIds.has(e.id)) 
                {
                    return false;
                }
                if (this.filters.noBucket && e.budgetBucket) {
                    return false;
                }

                if (desc) {
                    const description = e.description.toLowerCase();
                    for (let item of desc) {
                        let shouldBeIncluded = true;
                        if (item.startsWith("!")) {
                            shouldBeIncluded = false;
                            item = item.slice(1);
                        }
                        if (description.includes(item) !== shouldBeIncluded) {
                            return false;
                        }
                    }
                }

                if (tags) {
                    for (let item of tags) {
                        let shouldBeIncluded = true;
                        if (item.startsWith("!")) {
                            shouldBeIncluded = false;
                            item = item.slice(1);
                        }
                        const isIncluded = e.tags.some(it => it.includes(item));
                        if (isIncluded !== shouldBeIncluded) {
                            return false;
                        }
                    }
                }

                return true;
            });
        },
        input() {
            const input = this.$refs["file-input"] as HTMLInputElement;
            return input;
        },
        total(): string {
            return visualizeAmount(
                this.filteredExpenses.reduce(
                    (acc, next) => acc + next.rawAmount,
                    0
                )
            );
        },
        pageLinks(): {label: string, goTo: number | undefined}[] {
            const first = Math.max(0, this.currentPage - 2);
            const last = Math.min(this.totalPages-1, this.currentPage+2);
            const result: {label: string, goTo: number | undefined}[] = [];
            if (first > 0) {
                result.push({label: "...", goTo: 0});
            }
            for (let i = first; i <= last; i++) {
                result.push({label: (i+1).toString(), goTo: i});
            }
            if (last < this.totalPages-1) {
                result.push({label: "...", goTo: this.totalPages - 1});
            }
            return result;
        }
    },
});
